// Radha — AI layer reads + writes (migration 052): payment-date prediction,
// invoice anomaly detection (both server-computed RPCs, tenant-scoped by
// auth_tenant_id() same as every other read in this app), and promise-to-pay.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { fmtMoney } from "../lib/money";

export interface PaymentPrediction {
  invoice_id: string; predicted_pay_date: string; predicted_days_to_pay: number;
  confidence: string; basis: string;
}

export function usePaymentPredictions() {
  return useQuery({
    queryKey: ["payment-predictions"],
    queryFn: async (): Promise<Map<string, PaymentPrediction>> => {
      const { data, error } = await supabase.rpc("predict_payment_dates");
      if (error) throw error;
      return new Map((data as PaymentPrediction[] ?? []).map((p) => [p.invoice_id, p]));
    },
    staleTime: 60_000,
  });
}

export interface InvoiceAnomaly {
  invoice_id: string; invoice_number: string; customer_name: string;
  reason: string; severity: "warning" | "critical";
  // Amounts arrive as numerics rather than baked into `reason` (066), so they
  // can be rendered in the tenant's own currency.
  amount: string | null; baseline: string | null; multiple: string | null;
}

/** The human sentence for an anomaly, money formatted in the tenant's currency. */
export function anomalyText(a: InvoiceAnomaly): string {
  if (a.multiple && a.baseline) {
    return `${fmtMoney(a.amount)} is ${a.multiple}x this customer's average (${fmtMoney(a.baseline)})`;
  }
  return a.reason;
}

export function useInvoiceAnomalies() {
  return useQuery({
    queryKey: ["invoice-anomalies"],
    queryFn: async (): Promise<InvoiceAnomaly[]> => {
      const { data, error } = await supabase.rpc("detect_invoice_anomalies");
      if (error) throw error;
      return (data as InvoiceAnomaly[]) ?? [];
    },
    staleTime: 60_000,
  });
}

export interface Promise_ {
  id: string; account_id: string; promised_date: string; promised_amount: string;
  outstanding_at_promise: string; notes: string | null; created_at: string;
}

export function usePromises() {
  return useQuery({
    queryKey: ["promise-to-pay"],
    queryFn: async (): Promise<Promise_[]> => {
      const { data, error } = await supabase
        .from("promise_to_pay")
        .select("id, account_id, promised_date, promised_amount, outstanding_at_promise, notes, created_at")
        .order("promised_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

/** Judged against the account's CURRENT outstanding — a live snapshot, not a
 * ledger, so this is "as of now" not "as of promised_date" (documented at the
 * call site). pending until the promised date passes. */
export function promiseStatus(p: Promise_, currentOutstanding: number | undefined, today = new Date()): "pending" | "kept" | "broken" {
  const paidSince = Number(p.outstanding_at_promise) - (currentOutstanding ?? Number(p.outstanding_at_promise));
  if (paidSince >= Number(p.promised_amount) * 0.95) return "kept";
  if (new Date(p.promised_date) < today) return "broken";
  return "pending";
}

export function useLogPromise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { account_id: string; promised_date: string; promised_amount: number; notes?: string }) => {
      const { error } = await supabase.from("promise_to_pay").insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["promise-to-pay"] }),
  });
}

export function predictionErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
